import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  beginKeepsakePreview,
  fetchKeepsakeDelivery,
  pollKeepsakeJobStatus,
  requestKeepsakeExport,
  type KeepsakeClientError,
  type KeepsakePreviewBindingV1,
} from '@/lib/keepsake/client';
import { keepsakeLocaleFor } from '@/lib/keepsake/constants';
import { deliverKeepsake } from '@/lib/export-prayer';
import { KEEPSAKES_ENABLED } from '@/lib/core-cutover';
import styles from './PersonKeepsakeSheet.module.css';
import { useNoticeStackReserve } from '@/hooks/useNoticeStackReserve';
import { useUpdateHold } from '@/hooks/useUpdateHold';

/**
 * S0 SERVER-OWNED keepsake preview (PC-B rev 9, sections 10 + 10a.0).
 *
 * FULL REWRITE of the local-prop preview. This sheet NEVER receives live
 * prayer props: opening it creates a KeepsakePreviewBindingV1 (owner +
 * auth epoch + durable local prayer epoch captured BEFORE any prayer
 * display), fails closed on any unresolved local mutation fence or
 * validity intent (locked syncPending/protectionPending copy), and
 * renders ONLY the frozen KeepsakeRenderInputV1 the authenticated server
 * preview returned. Approval reuses that exact response; every result is
 * epoch-checked after every await; bindings never survive reload.
 *
 * The rendered dates are the server's preformatted labels — the sheet
 * performs no date formatting of its own.
 */

type SheetState =
  | { kind: 'loading' }
  | { kind: 'blocked'; code: KeepsakeClientError }
  | { kind: 'ready'; binding: KeepsakePreviewBindingV1 }
  | { kind: 'exporting'; binding: KeepsakePreviewBindingV1 }
  | { kind: 'polling'; binding: KeepsakePreviewBindingV1; jobId: string }
  | { kind: 'failed'; binding: KeepsakePreviewBindingV1; code: KeepsakeClientError }
  | { kind: 'email-fallback'; binding: KeepsakePreviewBindingV1 };

/** Closed error-code -> locked keepsake.* i18n key mapping (12b). */
const ERROR_KEY: Record<KeepsakeClientError, string> = {
  'not-signed-in': 'keepsake.accountChanged',
  'protection-pending': 'keepsake.protectionPending',
  'sync-pending': 'keepsake.syncPending',
  'account-changed': 'keepsake.accountChanged',
  'ownership-review-required': 'keepsake.ownershipReviewRequired',
  'no-prayers': 'keepsake.noPrayers',
  'general-unsupported': 'keepsake.generalUnsupported',
  maintenance: 'keepsake.maintenance',
  'client-update-required': 'keepsake.clientUpdateRequired',
  'preview-stale': 'keepsake.previewStale',
  'rate-limited': 'keepsake.rateLimited',
  'email-verification-required': 'keepsake.emailVerificationRequired',
  invalidated: 'keepsake.invalidated',
  'not-found': 'keepsake.noPrayers',
  'try-again': 'keepsake.tryAgain',
};

const POLL_INTERVAL_MS = 5_000;
const POLL_BUDGET_MS = 180_000;

interface PersonKeepsakeSheetProps {
  /** The raw target display (the server derives key + subject/display). */
  who: string;
  onClose: () => void;
  /** Coordinator-computed z for the `keepsake` layer root (A1). */
  zIndex?: number;
}

export default function PersonKeepsakeSheet({
  who,
  onClose,
  zIndex,
}: PersonKeepsakeSheetProps) {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState<SheetState>({ kind: 'loading' });
  const cancelledRef = useRef(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  // Bind + fetch the server preview on mount (and when who changes).
  useEffect(() => {
    let stale = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- the
    // effect IS the load trigger (rule #75 documented deviation).
    setState({ kind: 'loading' });
    // Core-content cutover: the client entry points throw when keepsakes
    // are disabled. Every mount path is already gated on the same
    // constant, so this catch is a backstop against an unhandled
    // rejection rather than a reachable branch.
    void beginKeepsakePreview(who, keepsakeLocaleFor(i18n.language))
      .then((result) => {
        if (stale || cancelledRef.current) return;
        if (result.ok) {
          setState({ kind: 'ready', binding: result.binding });
        } else {
          setState({ kind: 'blocked', code: result.code });
        }
      })
      .catch(() => {
        if (stale || cancelledRef.current) return;
        setState({ kind: 'blocked', code: 'maintenance' });
      });
    return () => {
      stale = true;
    };
  }, [who, i18n.language]);

  const binding =
    state.kind === 'ready' ||
    state.kind === 'exporting' ||
    state.kind === 'polling' ||
    state.kind === 'failed' ||
    state.kind === 'email-fallback'
      ? state.binding
      : null;
  const preview = binding?.serverPreview ?? null;

  const pollLoop = useCallback(
    async (activeBinding: KeepsakePreviewBindingV1, jobId: string) => {
      const startedAt = Date.now();
      for (;;) {
        if (cancelledRef.current) return;
        if (Date.now() - startedAt > POLL_BUDGET_MS) {
          setState({ kind: 'email-fallback', binding: activeBinding });
          return;
        }
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        if (cancelledRef.current) return;
        const status = await pollKeepsakeJobStatus(activeBinding, jobId);
        if (cancelledRef.current) return;
        if (!status.ok) {
          if (status.code === 'try-again') continue; // transient
          setState({ kind: 'failed', binding: activeBinding, code: status.code });
          return;
        }
        if (status.status === 'ready') {
          const delivery = await fetchKeepsakeDelivery(activeBinding, jobId);
          if (cancelledRef.current) return;
          if (!delivery.ok) {
            setState({ kind: 'failed', binding: activeBinding, code: delivery.code });
            return;
          }
          deliverKeepsake(delivery.blob, activeBinding.serverPreview?.displayName ?? '');
          setState({ kind: 'ready', binding: activeBinding });
          return;
        }
        if (
          status.status === 'failed' ||
          status.status === 'invalidated' ||
          status.status === 'expired' ||
          status.status === 'revoking'
        ) {
          setState({
            kind: 'failed',
            binding: activeBinding,
            code:
              status.errorCode === 'delivery-unknown'
                ? 'try-again'
                : status.status === 'failed'
                  ? 'try-again'
                  : 'invalidated',
          });
          return;
        }
        // publish_pending / queued / rendering: keep polling.
      }
    },
    [],
  );

  const onExport = async () => {
    // Cutover: the export button below is gated on the same constant;
    // this is the handler-side backstop.
    if (!KEEPSAKES_ENABLED) return;
    if (!binding) return;
    setState({ kind: 'exporting', binding });
    const result = await requestKeepsakeExport(binding);
    if (cancelledRef.current) return;
    if (!result.ok) {
      setState({ kind: 'failed', binding, code: result.code });
      return;
    }
    if (result.kind === 'pdf') {
      deliverKeepsake(result.blob, binding.serverPreview?.displayName ?? '');
      setState({ kind: 'ready', binding });
      return;
    }
    setState({ kind: 'polling', binding, jobId: result.jobId });
    void pollLoop(binding, result.jobId);
  };

  const heroTitle = preview
    ? t('people.keepsakeSheetTitle', { name: preview.displayName })
    : t('people.keepsakeSheetTitle', { name: who });

  const inFlight = state.kind === 'exporting' || state.kind === 'polling';
  // App-update safety: never reload the app while an export is in flight.
  useUpdateHold(inFlight, 'keepsake-export');
  // App notice stack: the in-flow spacer below the header keeps `.body` below
  // the fixed notices. Chrome only.
  const [noticeReserve, noticeReserveRef] = useNoticeStackReserve();
  const buttonLabel =
    state.kind === 'polling'
      ? t('people.keepsakeExportPreparing')
      : state.kind === 'exporting'
        ? t('people.keepsakeExporting')
        : t('people.keepsakeExportPdf');

  return (
    <div className={styles.root} style={{ zIndex }} role="dialog" aria-modal="true" aria-label={heroTitle}>
      <header className={styles.header}>
        <button
          type="button"
          className={styles.back}
          onClick={onClose}
          aria-label={t('nav.back')}
        >
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <polyline points="14 6 8 12 14 18" />
          </svg>
        </button>
        <h1 className={styles.headerTitle}>{heroTitle}</h1>
      </header>
      <div
        ref={noticeReserveRef}
        className={styles.noticeReserve}
        style={{ height: noticeReserve }}
        data-notice-reserve={noticeReserve}
        aria-hidden="true"
      />

      <div className={styles.body}>
        {state.kind === 'loading' && (
          <p className={styles.intro}>{t('people.keepsakePreviewLoading')}</p>
        )}

        {state.kind === 'blocked' && (
          <p className={styles.privacyNote} role="status">
            {t(ERROR_KEY[state.code])}
          </p>
        )}

        {preview && (
          <>
            <section className={styles.hero}>
              <h2 className={styles.heroTitle}>{heroTitle}</h2>
              <p className={styles.heroSubtitle}>
                {t('people.keepsakeSubtitle', {
                  count: preview.membershipCount,
                  span: preview.renderInput.coverSpanLabel || '—',
                })}
              </p>
            </section>

            <p className={styles.intro}>{t('people.keepsakePreviewIntro')}</p>

            <ol className={styles.list}>
              {preview.renderInput.prayers.map((p) => (
                <li key={p.id} className={styles.entry}>
                  <span className={styles.entryDate}>{p.renderedCreatedLabel}</span>
                  <p className={styles.entryText}>{p.rawText}</p>
                  {p.renderedTestimony !== null && (
                    <div className={styles.entryTestimony}>
                      <span className={styles.entryTestimonyLabel}>
                        {t('people.keepsakeTestimonyPrefix')}
                      </span>
                      {p.renderedTestimony}
                    </div>
                  )}
                </li>
              ))}
            </ol>

            <p className={styles.footer}>{t('people.keepsakeComingSoon')}</p>
          </>
        )}
      </div>

      {preview && (
        <div className={styles.actionBar}>
          <div>
            {state.kind === 'email-fallback' ? (
              <p className={styles.exportError}>
                {t('people.keepsakeExportEmailFallback')}
              </p>
            ) : (
              <>
                {KEEPSAKES_ENABLED && (
                <button
                  type="button"
                  className={styles.exportBtn}
                  disabled={inFlight}
                  onClick={onExport}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  {buttonLabel}
                </button>
                )}
                {state.kind === 'failed' && (
                  <p className={styles.exportError}>{t(ERROR_KEY[state.code])}</p>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
